// Shared trip-date system used everywhere a customer enters pickup / return
// dates. Designed to be timezone-safe and to persist a single source of
// truth across pages so a customer never has to re-enter the dates they
// already picked.
//
// All dates are YYYY-MM-DD strings (local TZ), all times are HH:mm strings.
// Never round-trip date-only values through `new Date(s).toISOString()` —
// that shifts by timezone. Use the helpers below.

const KEY = "saved-dates";
const LEGACY_KEY = "sprinter-trip-dates"; // older name, migrate on read
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export interface TripDates {
  pickupDate: string;
  returnDate: string;
  pickupTime: string;
  returnTime: string;
  passengerCount?: number;
  savedAt?: number;
}

// ──────────────────────────────────────────────────────────────────────────
// Pure date helpers (timezone-safe; all strings are YYYY-MM-DD in local TZ)
// ──────────────────────────────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Normalize any time string to 24-hour "HH:mm".
 * Accepts "1:30 PM", "01:30pm", "13:30", "13:30:00". Returns "" for junk so
 * callers can fall back to their own default.
 */
export function to24h(value: string | null | undefined): string {
  if (!value) return "";
  const s = String(value).trim();
  const m = s.match(/^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*([AaPp][Mm])?$/);
  if (!m) return "";
  let h = Number(m[1]);
  const min = m[2] ?? "00";
  const mer = m[3]?.toLowerCase();
  if (mer === "am") h = h === 12 ? 0 : h;
  else if (mer === "pm") h = h === 12 ? 12 : h + 12;
  if (!Number.isFinite(h) || h < 0 || h > 23) return "";
  return `${pad(h)}:${min}`;
}


export function toYmd(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function todayYmd(): string {
  return toYmd(new Date());
}

/** Parse a YYYY-MM-DD string as a local-midnight Date (no UTC shift). */
export function parseYmd(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function addDaysYmd(s: string, n: number): string {
  const d = parseYmd(s);
  d.setDate(d.getDate() + n);
  return toYmd(d);
}

// Lexicographic comparison is safe because YYYY-MM-DD is sortable as string.
export const isSameYmd = (a: string, b: string) => Boolean(a) && a === b;
export const isAfterYmd = (a: string, b: string) => Boolean(a && b) && a > b;
export const isBeforeYmd = (a: string, b: string) => Boolean(a && b) && a < b;
export const isTodayOrAfterYmd = (s: string) => Boolean(s) && s >= todayYmd();

/** Whole-day count between two YYYY-MM-DD strings. 0 when invalid. */
export function daysBetweenYmd(pickup: string, ret: string): number {
  if (!pickup || !ret) return 0;
  const p = parseYmd(pickup).getTime();
  const r = parseYmd(ret).getTime();
  const diff = Math.round((r - p) / 86400000);
  return diff > 0 ? diff : 0;
}

/** Validation used by every form: return strictly after pickup, both present. */
export function areDatesValid(pickup: string, ret: string): boolean {
  return Boolean(pickup && ret) && isAfterYmd(ret, pickup);
}

// ──────────────────────────────────────────────────────────────────────────
// localStorage persistence
// ──────────────────────────────────────────────────────────────────────────

function readRaw(): Partial<TripDates> {
  if (typeof window === "undefined") return {};
  try {
    let raw = localStorage.getItem(KEY);
    if (!raw) {
      // One-time migration from the older key name.
      const legacy = localStorage.getItem(LEGACY_KEY);
      if (legacy) {
        raw = legacy;
        try { localStorage.setItem(KEY, legacy); } catch { /* noop */ }
        try { localStorage.removeItem(LEGACY_KEY); } catch { /* noop */ }
      }
    }
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/** Read saved dates, dropping anything stale or in the past. */
export function getSavedDates(): Partial<TripDates> {
  const raw = readRaw();
  // Legacy entries may hold 12-hour times ("12:00 PM"); normalize on read.
  const data: Partial<TripDates> = {
    ...raw,
    ...(raw.pickupTime ? { pickupTime: to24h(raw.pickupTime) || raw.pickupTime } : {}),
    ...(raw.returnTime ? { returnTime: to24h(raw.returnTime) || raw.returnTime } : {}),
  };

  // Expire after 30 days.
  if (data.savedAt && Date.now() - data.savedAt > TTL_MS) {
    clearSavedDates();
    return {};
  }
  // If the pickup is already in the past, drop the dates so the user is
  // prompted for fresh ones (but keep the time defaults if any).
  if (data.pickupDate && !isTodayOrAfterYmd(data.pickupDate)) {
    const { pickupDate: _p, returnDate: _r, ...rest } = data;
    return rest;
  }
  // If return date is no longer valid relative to pickup, drop it.
  if (data.pickupDate && data.returnDate && !isAfterYmd(data.returnDate, data.pickupDate)) {
    const { returnDate: _r, ...rest } = data;
    return rest;
  }
  return data;
}

// Backwards-compatible alias used by older imports.
export const loadTripDates = getSavedDates;

export function saveDates(patch: Partial<TripDates>): void {
  if (typeof window === "undefined") return;
  try {
    const current = readRaw();
    const next: Partial<TripDates> = { ...current, ...patch };
    // Times are stored in 24-hour HH:mm so every consumer (and Date parsing)
    // agrees. Widgets may hand us "12:00 PM".
    if (next.pickupTime) next.pickupTime = to24h(next.pickupTime) || next.pickupTime;
    if (next.returnTime) next.returnTime = to24h(next.returnTime) || next.returnTime;
    // Drop empty strings / nullish values so we never overwrite a good value
    // with a blank one.
    for (const k of Object.keys(next) as (keyof TripDates)[]) {
      const v = next[k];
      if (v === "" || v == null) delete next[k];
    }
    next.savedAt = Date.now();

    localStorage.setItem(KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent("tripdates:change"));
  } catch {
    /* noop */
  }
}

// Backwards-compatible alias used by older imports.
export const saveTripDates = saveDates;

export function clearSavedDates(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(LEGACY_KEY);
    window.dispatchEvent(new CustomEvent("tripdates:change"));
  } catch {
    /* noop */
  }
}

/** React hook: subscribes to tripDates changes (in this tab + across tabs). */
export function tripDatesEventName() {
  return "tripdates:change" as const;
}
